import React, { useState, useEffect, useRef } from 'react';
import './index.css';

// Contexts & Views
import { AuthProvider, useAuth } from './AuthContext';
import { applyDocumentMeta } from './seoMeta';
import HomePage from './HomePage';
import { placeForPath } from './seoPlaces';
import useIsMobile from './useIsMobile';
import LoginScreen from './Login';
import Dashboard from './Dashboard';
import DetailsPage from './DetailsPage';
import ChatWidget from './ChatWidget';
import OnboardingScreen from './OnboardingScreen';
import PostListingPage from './PostListingPage';
import PostRequirementPage from './PostRequirementPage';
import MyListingsPage from './MyListingsPage';
import MyRequirementPage from './MyRequirementPage';
import ProfilePage from './ProfilePage';
import LegalPage from './LegalPage';
import AboutPage from './AboutPage';
import AppDownloadBanner from './AppDownloadBanner';
import RentContributionPage from './RentContributionPage';
import AreaInsightsPage from './AreaInsightsPage';
import { PrimeModalHost } from './PrimeModal';

// Components
import { S3_BASE_URL } from './api';
import {
  ContactForm,
} from './components';

// === SITE NAVIGATION ===
const TopNav = ({ onLoginClick, onNavigateRoute, currentRoute }) => {
  const { isAuthenticated, logout, user, isListingUpload, isFlatNeedUpload } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const postRef = useRef(null);

  const isMobile = useIsMobile(768);

  const hasListing = isListingUpload;
  const hasRequirement = isFlatNeedUpload;

  // Scroll detection for nav style switch
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowProfileMenu(false);
      if (postRef.current && !postRef.current.contains(event.target)) setShowPostMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hiddenRoutes = ['myListings', 'myRequirement', 'editListing', 'editRequirement'];
  if (hiddenRoutes.includes(currentRoute)) return null;

  // True when nav sits over the blue hero gradient
  // 'home' is deliberately absent: the redesigned home page opens on a light
  // wash, not the old dark-blue hero, so the white-on-blue nav treatment would
  // render the logo and every action invisible until you scrolled. The other
  // routes here still have dark headers.
  const blueRoutes = ['dashboard', 'details', 'about', 'postListing', 'postRequirement', 'profile', 'rentInsights', 'contributeRent'];
  const isOnBlue = blueRoutes.includes(currentRoute) && !scrolled;

  const menuItemStyle = () => ({
    padding: '11px 14px', display: 'flex', alignItems: 'center',
    gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.15s',
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      padding: isMobile ? (isOnBlue ? '10px 12px 0' : '6px 12px 0') : (isOnBlue ? '18px clamp(16px, 3vw, 40px) 0' : '10px clamp(16px, 3vw, 40px) 0'),
      transition: 'padding 0.3s ease',
      pointerEvents: 'none'
    }}>
      <nav style={{
        margin: '0 auto', maxWidth: '1400px', pointerEvents: 'all',
        background: isOnBlue
          ? 'rgba(255,255,255,0.18)'
          : 'rgba(255,255,255,0.96)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        borderRadius: isMobile ? '16px' : '20px', padding: isMobile ? '8px 12px' : '11px 22px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        border: isOnBlue
          ? '1px solid rgba(255,255,255,0.38)'
          : '1px solid rgba(37,99,235,0.10)',
        boxShadow: isOnBlue
          ? '0 8px 32px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.3)'
          : '0 8px 32px rgba(37,99,235,0.08)',
        transition: 'all 0.3s ease',
      }}>
        {/* LOGO */}
        <div onClick={() => onNavigateRoute('home')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', userSelect: 'none' }}>
          <img src="/flatloop.png" alt="FlatLoop" style={{ height: '32px', objectFit: 'contain', filter: isOnBlue ? 'brightness(0) invert(1)' : 'none', transition: 'filter 0.3s' }} />
          <span style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.5px', color: isOnBlue ? 'white' : '#2563EB' }}>Flat<span style={{ color: isOnBlue ? 'rgba(255,255,255,0.85)' : '#10B981' }}>Loop</span></span>
        </div>

        {/* RIGHT ACTIONS */}
        <div style={{ display: 'flex', gap: isMobile ? '6px' : '10px', alignItems: 'center' }}>
          {isAuthenticated ? (
            <>
              {/* Browse/Dashboard toggle */}
              {!isMobile && <button
                onClick={() => currentRoute === 'dashboard' ? onNavigateRoute('home') : onNavigateRoute('dashboard')}
                style={{
                  background: isOnBlue ? 'rgba(255,255,255,0.22)' : '#F1F5FF',
                  color: isOnBlue ? 'white' : '#2563EB',
                  border: isOnBlue ? '1px solid rgba(255,255,255,0.38)' : '1px solid #DBEAFE',
                  padding: '9px 18px', borderRadius: '12px',
                  fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.opacity = '0.8'}
                onMouseOut={e => e.currentTarget.style.opacity = '1'}
              >
                {currentRoute === 'dashboard' ? '← Home' : 'Browse'}
              </button>}

              {/* Post + Dropdown */}
              <div style={{ position: 'relative' }} ref={postRef}>
                <button
                  onClick={() => setShowPostMenu(!showPostMenu)}
                  style={{
                    background: isOnBlue
                      ? 'rgba(255,255,255,0.92)'
                      : 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                    color: isOnBlue ? '#1E40AF' : 'white',
                    border: 'none',
                    padding: isMobile ? '8px 14px' : '9px 18px', borderRadius: '12px',
                    fontSize: isMobile ? '12px' : '13px', fontWeight: '800', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '7px',
                    boxShadow: isOnBlue
                      ? '0 4px 14px rgba(0,0,0,0.15)'
                      : '0 4px 14px rgba(37,99,235,0.35)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseOut={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Post
                </button>

                {showPostMenu && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 12px)', right: isMobile ? '-60px' : 0, width: isMobile ? '260px' : '290px', background: 'white', borderRadius: '20px', padding: '10px', border: '1px solid #E5E7EB', boxShadow: '0 24px 48px rgba(0,0,0,0.12)', zIndex: 200 }}>
                    <style>{`@keyframes dropIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }`}</style>
                    <div style={{ animation: 'dropIn 0.18s ease-out' }}>

                      {/* Case 1: User has a requirement → only show "My Requirement" */}
                      {hasRequirement && (
                        <>
                          <div style={{ padding: '12px 16px', background: '#FFF7ED', borderRadius: '12px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '16px' }}>ℹ️</span>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#92400E' }}>You've already posted a requirement. You can update it below.</span>
                          </div>
                          <div
                            onClick={() => { setShowPostMenu(false); onNavigateRoute('myRequirement'); }}
                            style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#FFF7ED'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #F97316, #FB923C)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📣</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>My Requirement</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>View & update your requirement</div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Case 2: User has listings → show "Post New Listing" + "My Listings" */}
                      {hasListing && !hasRequirement && (
                        <>
                          <div
                            onClick={() => { setShowPostMenu(false); onNavigateRoute('postListing'); }}
                            style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>➕</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Post New Listing</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>Add another property</div>
                            </div>
                          </div>
                          <div
                            onClick={() => { setShowPostMenu(false); onNavigateRoute('myListings'); }}
                            style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🏠</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>My Listings</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>Manage your posted properties</div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Case 3: Fresh user → show both options */}
                      {!hasListing && !hasRequirement && (
                        <>
                          <div
                            onClick={() => { setShowPostMenu(false); onNavigateRoute('postListing'); }}
                            style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🏠</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>I have a property</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>List your flat, room or PG</div>
                            </div>
                          </div>
                          <div
                            onClick={() => { setShowPostMenu(false); onNavigateRoute('postRequirement'); }}
                            style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#FFF7ED'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #F97316, #FB923C)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🔍</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>I'm looking for a place</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>Post your requirement</div>
                            </div>
                          </div>
                          <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#9CA3AF', fontSize: '12px', fontWeight: '500' }}>
                            <span>ℹ️</span> You can either list properties or post a requirement, not both.
                          </div>
                          <div style={{ height: '1px', background: '#F3F4F6', margin: '4px 8px' }} />
                          <div
                            onClick={() => { setShowPostMenu(false); onNavigateRoute('contributeRent'); }}
                            style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#ECFDF5'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📊</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Share Rent Data</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>Help build area pricing</div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Profile Pill */}
              <div style={{ position: 'relative' }} ref={dropdownRef}>
                <div
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  style={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px',
                    background: isOnBlue ? 'rgba(255,255,255,0.22)' : 'white',
                    padding: isMobile ? '5px 8px 5px 5px' : '5px 14px 5px 5px', borderRadius: '40px',
                    border: isOnBlue ? '1px solid rgba(255,255,255,0.38)' : `1px solid ${showProfileMenu ? '#2563EB' : '#E5E7EB'}`,
                    boxShadow: isOnBlue ? '0 4px 16px rgba(0,0,0,0.15)' : (showProfileMenu ? '0 4px 16px rgba(37,99,235,0.12)' : '0 2px 8px rgba(0,0,0,0.04)'),
                    transition: 'all 0.2s'
                  }}
                >
                  {user?.profilePicUrl ? (
                    <img src={`${S3_BASE_URL}${user.profilePicUrl}`} alt="User" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #2563EB, #3B82F6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /></svg>
                    </div>
                  )}
                  {!isMobile && <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '13px', fontWeight: '800', color: isOnBlue ? 'white' : '#111827', whiteSpace: 'nowrap' }}>{user?.name || 'My Profile'}</span>
                    <span style={{ fontSize: '11px', fontWeight: '500', color: isOnBlue ? 'rgba(255,255,255,0.65)' : '#9CA3AF' }}>FlatLoop</span>
                  </div>}
                  <div style={{ color: isOnBlue ? 'rgba(255,255,255,0.7)' : '#9CA3AF', display: 'flex', transform: showProfileMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                  </div>
                </div>

                {showProfileMenu && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 12px)', right: isMobile ? '-12px' : 0, width: '230px', background: 'white', borderRadius: '20px', padding: '8px', border: '1px solid #E5E7EB', boxShadow: '0 24px 48px rgba(0,0,0,0.12)', zIndex: 200, animation: 'dropIn 0.18s ease-out' }}>
                    {/* User info header */}
                    <div style={{ padding: '12px 14px', marginBottom: '4px', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ fontWeight: '800', fontSize: '14px', color: '#111827' }}>{user?.name || 'FlatLoop User'}</div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>{user?.phoneNumber || 'Verified Member'}</div>
                    </div>

                    <div onClick={() => { setShowProfileMenu(false); onNavigateRoute('profile'); }}
                      style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>View Profile</span>
                    </div>

                    {hasListing && (
                      <div onClick={() => { setShowProfileMenu(false); onNavigateRoute('myListings'); }}
                        style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.15s' }}
                        onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#ECFDF5', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>🏠</div>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>My Listings</span>
                      </div>
                    )}

                    {hasRequirement && (
                      <div onClick={() => { setShowProfileMenu(false); onNavigateRoute('myRequirement'); }}
                        style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.15s' }}
                        onMouseOver={e => e.currentTarget.style.background = '#FFF7ED'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#FFF7ED', color: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>📣</div>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>My Requirement</span>
                      </div>
                    )}

                    <div style={{ height: '1px', background: '#F3F4F6', margin: '6px 8px' }} />
                    <div onClick={() => { setShowProfileMenu(false); logout(); onNavigateRoute('home'); }}
                      style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#FEF2F2'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: '#FEF2F2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#DC2626' }}>Sign Out</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={onLoginClick}
                style={{
                  background: isOnBlue ? 'rgba(255,255,255,0.18)' : 'transparent',
                  color: isOnBlue ? 'white' : '#374151',
                  border: isOnBlue ? '1px solid rgba(255,255,255,0.38)' : '1px solid #E5E7EB',
                  padding: isMobile ? '8px 14px' : '9px 20px', borderRadius: '12px',
                  fontSize: isMobile ? '12px' : '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.opacity = '0.8'}
                onMouseOut={e => e.currentTarget.style.opacity = '1'}
              >
                Sign In
              </button>
              <a
                href="#download"
                style={{
                  background: isOnBlue ? 'white' : 'linear-gradient(135deg, #1E40AF, #2563EB)',
                  color: isOnBlue ? '#1E40AF' : 'white',
                  border: 'none', padding: isMobile ? '8px 14px' : '9px 20px', borderRadius: '12px',
                  fontSize: isMobile ? '12px' : '13px', fontWeight: '700', cursor: 'pointer',
                  textDecoration: 'none',
                  boxShadow: isOnBlue ? '0 4px 16px rgba(0,0,0,0.15)' : '0 4px 14px rgba(37,99,235,0.35)',
                  transition: 'all 0.2s'
                }}
              >
                Get App
              </a>
            </>
          )}
        </div>
      </nav>
    </div>
  );
};

// === FOOTER ===
const FooterNav = ({ onNavigate }) => (
  <footer style={{ background: 'linear-gradient(155deg, #1E3A8A 0%, #1E40AF 60%, #1D4ED8 100%)', color: 'white', padding: '48px 20px 24px' }}>
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 4px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '48px', marginBottom: '40px', justifyContent: 'space-between' }}>
        {/* Brand */}
        <div style={{ flex: '2 1 280px' }}>
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <img src="/flatloop.png" alt="FlatLoop Logo" style={{ height: '36px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          </div>
          <p style={{ color: '#BFDBFE', fontSize: '14px', maxWidth: '300px', lineHeight: '1.7' }}>
            Find flatmates, roommates, shared flats, PG & rooms for rent across 10 Indian cities. Every account is phone-verified. Zero brokerage, zero commission.
          </p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            {['FB', 'TW', 'IN', 'IG'].map(s => (
              <div key={s} style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800', color: 'white', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)' }}>{s}</div>
            ))}
          </div>
        </div>

        {/* Company */}
        <div style={{ flex: '1 1 150px' }}>
          <h4 style={{ color: 'white', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px' }}>Company</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              ['About Us', () => onNavigate && onNavigate('about')],
              ['Our Features', '#features'],
              ['How It Works', '#how-it-works'],
              ['Cities We Serve', '#cities'],
            ].map(([label, action]) => (
              <li key={label}>
                {typeof action === 'string' ? (
                  <a href={action} style={{ color: '#BFDBFE', textDecoration: 'none', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>{label}</a>
                ) : (
                  <button onClick={action} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '14px', fontWeight: '500', cursor: 'pointer', padding: 0, transition: 'color 0.2s', fontFamily: 'inherit' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>{label}</button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div style={{ flex: '1 1 150px' }}>
          <h4 style={{ color: 'white', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px' }}>Resources</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              ['Find a Flat', () => onNavigate && onNavigate('dashboard')],
              ['Post a Listing', () => onNavigate && onNavigate('postListing')],
              ['Post Requirement', () => onNavigate && onNavigate('postRequirement')],
              ['Rent Insights', () => onNavigate && onNavigate('rentInsights')],
              ['Share Rent Data', () => onNavigate && onNavigate('contributeRent')],
              ['Download App', '#download'],
              ['Help &amp; FAQs', '#contact'],
            ].map(([label, action]) => (
              <li key={label}>
                {typeof action === 'string' ? (
                  <a href={action} style={{ color: '#BFDBFE', textDecoration: 'none', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'} dangerouslySetInnerHTML={{ __html: label }} />
                ) : (
                  <button onClick={action} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '14px', fontWeight: '500', cursor: 'pointer', padding: 0, transition: 'color 0.2s', fontFamily: 'inherit' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>{label}</button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Legal */}
        <div style={{ flex: '1 1 150px' }}>
          <h4 style={{ color: 'white', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px' }}>Legal</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              ['Privacy Policy', () => onNavigate && onNavigate('privacy')],
              ['Terms &amp; Conditions', () => onNavigate && onNavigate('terms')],
              ['Refund &amp; Cancellation', () => onNavigate && onNavigate('refund')],
              ['Pricing', () => onNavigate && onNavigate('pricing')],
              ['Contact Us', '#contact'],
            ].map(([label, action]) => (
              <li key={label}>
                {typeof action === 'string' ? (
                  <a href={action} style={{ color: '#BFDBFE', textDecoration: 'none', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'} dangerouslySetInnerHTML={{ __html: label }} />
                ) : (
                  <button onClick={action} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '14px', fontWeight: '500', cursor: 'pointer', padding: 0, transition: 'color 0.2s', fontFamily: 'inherit' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'} dangerouslySetInnerHTML={{ __html: label }} />
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Top Cities SEO — single line */}
      <div style={{ marginBottom: '32px', paddingBottom: '28px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', marginRight: '12px' }}>Top Cities:</span>
        {/* Real anchors, not text: these were spans, so no city page had a single
            crawlable link pointing at it from anywhere but the home page. Pages
            still below the inventory bar keep nofollow (see seoMeta setRobots). */}
        {[['Flats in Delhi', 'delhi'], ['Flats in Noida', 'noida'], ['Flats in Gurgaon', 'gurgaon'], ['Flats in Bangalore', 'bangalore'],
          ['Flats in Mumbai', 'mumbai'], ['Flats in Pune', 'pune'], ['Flats in Hyderabad', 'hyderabad'], ['Flats in Chennai', 'chennai'],
          ['Flats in Kolkata', 'kolkata'], ['Flats in Ahmedabad', 'ahmedabad']].map(([label, slug], i, arr) => {
          const place = placeForPath(`/flats-in-${slug}`);
          return (
            <span key={slug}>
              <a href={`/flats-in-${slug}`} rel={place && !place.index ? 'nofollow' : undefined} style={{ color: '#BFDBFE', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}>{label}</a>
              {i < arr.length - 1 && <span style={{ color: 'rgba(255,255,255,0.25)', margin: '0 8px' }}>·</span>}
            </span>
          );
        })}
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '28px', display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ color: '#BFDBFE', fontSize: '13px', margin: 0 }}>&copy; {new Date().getFullYear()} FlatLoop India — Flatmate Finder & Shared Flats App. All rights reserved.</p>
        <div style={{ display: 'flex', gap: '24px' }}>
          <button onClick={() => onNavigate && onNavigate('privacy')} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>Privacy</button>
          <button onClick={() => onNavigate && onNavigate('terms')} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>Terms</button>
          <button onClick={() => onNavigate && onNavigate('refund')} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>Refunds</button>
          <button onClick={() => onNavigate && onNavigate('pricing')} style={{ background: 'none', border: 'none', color: '#BFDBFE', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>Pricing</button>
          <a href="#contact" style={{ color: '#BFDBFE', fontSize: '13px', textDecoration: 'none', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = '#BFDBFE'}>Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

// MobileFab removed — replaced by AppDownloadBanner

// === MAIN RENDER APP ===
const MainApp = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(() => {
    const path = window.location.pathname;
    if (path.includes('/details')) return 'details';
    if (path.includes('/search')) return 'dashboard';
    if (path.includes('/post-listing')) return 'postListing';
    if (path.includes('/post-requirement')) return 'postRequirement';
    if (path.includes('/rent-insights')) return 'rentInsights';
    if (path.includes('/contribute-rent')) return 'contributeRent';
    if (path.includes('/my-listings')) return 'myListings';
    if (path.includes('/my-requirement')) return 'myRequirement';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/terms')) return 'terms';
    if (path.includes('/privacy')) return 'privacy';
    if (path.includes('/refund')) return 'refund';
    if (path.includes('/pricing')) return 'pricing';
    if (path.includes('/about')) return 'about';
    // /flats-in-<slug> is a search result page wearing an SEO URL.
    if (placeForPath(path)) return 'dashboard';
    return 'home';
  });
  // Two place URLs share one route ('dashboard'), so keying the head effect on
  // the route alone left /flats-in-bangalore showing Chennai's title, canonical
  // and robots after a back/forward between them. The pathname is tracked
  // separately for that reason.
  const [pathname, setPathname] = useState(window.location.pathname);

  // Seeded synchronously from the URL rather than in an effect: Dashboard reads
  // initialLocation on its very first render, and the prerender snapshots the
  // page as soon as the network settles. Setting this a tick later would bake
  // Delhi's listings into every locality page.
  const [initialLocation, setInitialLocation] = useState(() => {
    const place = placeForPath(window.location.pathname);
    return place ? { name: place.name, lat: place.lat, lng: place.lng } : null;
  });
  const [editListingData, setEditListingData] = useState(null);
  const [editRequirementData, setEditRequirementData] = useState(null);

  useEffect(() => {
    const handlePopState = () => {
      if (window.location.pathname.includes('/details')) setCurrentRoute('details');
      else if (window.location.pathname.includes('/search')) setCurrentRoute('dashboard');
      else if (window.location.pathname.includes('/post-listing')) setCurrentRoute('postListing');
      else if (window.location.pathname.includes('/post-requirement')) setCurrentRoute('postRequirement');
      else if (window.location.pathname.includes('/rent-insights')) setCurrentRoute('rentInsights');
      else if (window.location.pathname.includes('/contribute-rent')) setCurrentRoute('contributeRent');
      else if (window.location.pathname.includes('/my-listings')) setCurrentRoute('myListings');
      else if (window.location.pathname.includes('/my-requirement')) setCurrentRoute('myRequirement');
      else if (window.location.pathname.includes('/profile')) setCurrentRoute('profile');
      else if (window.location.pathname.includes('/terms')) setCurrentRoute('terms');
      else if (window.location.pathname.includes('/privacy')) setCurrentRoute('privacy');
      else if (window.location.pathname.includes('/refund')) setCurrentRoute('refund');
      else if (window.location.pathname.includes('/pricing')) setCurrentRoute('pricing');
      else if (window.location.pathname.includes('/about')) setCurrentRoute('about');
      else if (placeForPath(window.location.pathname)) setCurrentRoute('dashboard');
      else setCurrentRoute('home');

      // Back/forward across place URLs has to move the map as well as the head.
      setPathname(window.location.pathname);
      const place = placeForPath(window.location.pathname);
      if (place) setInitialLocation({ name: place.name, lat: place.lat, lng: place.lng });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Retitle the document whenever the route changes. Navigation here is
  // history.pushState plus a setState, which does not touch <head> on its own,
  // so without this every page in the site kept the home page's title and
  // description — in the tab, in browser history, and in the preview card when
  // someone pastes the link into WhatsApp.
  useEffect(() => {
    applyDocumentMeta(window.location.pathname);
  }, [currentRoute, pathname]);

  const { isAuthenticated, loading, isRegisteredUser } = useAuth();
  const initialRedirectDone = useRef(false);

  useEffect(() => {
    if (!loading && !initialRedirectDone.current) {
      if (isAuthenticated && currentRoute === 'home') {
        window.history.replaceState({}, '', '/search');
        setCurrentRoute('dashboard');
      }
      initialRedirectDone.current = true;
    }
  }, [loading, isAuthenticated, currentRoute]);

  const handleNavigate = (route, data = null) => {
    const routeMap = {
      dashboard: '/search',
      home: '/',
      about: '/about',
      postListing: '/post-listing',
      postRequirement: '/post-requirement',
      myListings: '/my-listings',
      myRequirement: '/my-requirement',
      profile: '/profile',
      editListing: '/post-listing',
      editRequirement: '/post-requirement',
      rentInsights: '/rent-insights',
      contributeRent: '/contribute-rent',
      terms: '/terms',
      privacy: '/privacy',
      refund: '/refund-policy',
      pricing: '/pricing',
    };
    window.history.pushState({}, '', routeMap[route] || '/');
    setPathname(routeMap[route] || '/');
    setCurrentRoute(route);

    if (route === 'editListing') {
      setEditListingData(data);
      setCurrentRoute('postListing');
    }
    if (route === 'editRequirement') {
      setEditRequirementData(data);
      setCurrentRoute('postRequirement');
    }
    window.scrollTo(0, 0);
  };

  // Render fullscreen pages (no nav/footer)
  const fullscreenRoutes = ['myListings', 'myRequirement', 'terms', 'privacy', 'refund', 'pricing'];
  const isFullscreen = fullscreenRoutes.includes(currentRoute);

  return (
    <>
      <TopNav
        onLoginClick={() => setShowLogin(true)}
        onNavigateRoute={handleNavigate}
        currentRoute={currentRoute}
      />

      {isAuthenticated && !loading && !isRegisteredUser && (
        <OnboardingScreen />
      )}

      {/* Login Modal Flow */}
      {showLogin && (
        <LoginScreen
          onBack={() => setShowLogin(false)}
          onComplete={(isNewUser) => {
            setShowLogin(false);
            handleNavigate('dashboard');
          }}
          onNavigate={(route) => {
            setShowLogin(false);
            handleNavigate(route);
          }}
        />
      )}

      {currentRoute === 'details' ? (
        <main id="main-content" role="main">
          <DetailsPage onRequireLogin={() => setShowLogin(true)} />
        </main>
      ) : currentRoute === 'dashboard' ? (
        <main id="main-content" role="main">
          <Dashboard onRequireLogin={() => setShowLogin(true)} initialLocation={initialLocation} onNavigate={handleNavigate} place={placeForPath(pathname)} />
        </main>
      ) : currentRoute === 'postListing' ? (
        <main id="main-content" role="main">
          <PostListingPage
            onBack={() => handleNavigate('dashboard')}
            editListing={editListingData}
          />
        </main>
      ) : currentRoute === 'postRequirement' ? (
        <main id="main-content" role="main">
          <PostRequirementPage
            onBack={() => handleNavigate('dashboard')}
            editRequirement={editRequirementData}
          />
        </main>
      ) : currentRoute === 'myListings' ? (
        <main id="main-content" role="main">
          <MyListingsPage
            onBack={() => handleNavigate('dashboard')}
            onEditListing={(listing) => {
              if (listing) {
                setEditListingData(listing);
                handleNavigate('postListing');
              } else {
                setEditListingData(null);
                handleNavigate('postListing');
              }
            }}
          />
        </main>
      ) : currentRoute === 'myRequirement' ? (
        <main id="main-content" role="main">
          <MyRequirementPage
            onBack={() => handleNavigate('dashboard')}
            onEditRequirement={(req) => {
              setEditRequirementData(req);
              handleNavigate('postRequirement');
            }}
            onPostNew={() => {
              setEditRequirementData(null);
              handleNavigate('postRequirement');
            }}
          />
        </main>
      ) : currentRoute === 'profile' ? (
        <main id="main-content" role="main">
          <ProfilePage
            onBack={() => handleNavigate('dashboard')}
            onNavigate={(route) => handleNavigate(route)}
          />
        </main>
      ) : currentRoute === 'terms' ? (
        <main id="main-content" role="main">
          <LegalPage
            pageType="terms"
            onBack={() => handleNavigate('home')}
            onSwitchPage={(type) => handleNavigate(type)}
          />
        </main>
      ) : currentRoute === 'privacy' ? (
        <main id="main-content" role="main">
          <LegalPage
            pageType="privacy"
            onBack={() => handleNavigate('home')}
            onSwitchPage={(type) => handleNavigate(type)}
          />
        </main>
      ) : currentRoute === 'refund' ? (
        <main id="main-content" role="main">
          <LegalPage
            pageType="refund"
            onBack={() => handleNavigate('home')}
            onSwitchPage={(type) => handleNavigate(type)}
          />
        </main>
      ) : currentRoute === 'pricing' ? (
        <main id="main-content" role="main">
          <LegalPage
            pageType="pricing"
            onBack={() => handleNavigate('home')}
            onSwitchPage={(type) => handleNavigate(type)}
          />
        </main>
      ) : currentRoute === 'about' ? (
        <main id="main-content" role="main">
          <AboutPage onBack={() => handleNavigate('home')} />
        </main>
      ) : currentRoute === 'rentInsights' ? (
        <main id="main-content" role="main">
          <AreaInsightsPage
            initialLocation={initialLocation}
            onNavigate={handleNavigate}
          />
        </main>
      ) : currentRoute === 'contributeRent' ? (
        <main id="main-content" role="main">
          <RentContributionPage
            onRequireLogin={() => setShowLogin(true)}
            onNavigate={handleNavigate}
          />
        </main>
      ) : (
        <main id="main-content" role="main">
          {/* One component now, not seven sections. The old TestimonialsSection
              is deliberately NOT carried over: it published four invented reviews
              attributed to named people with five-star ratings under the heading
              "Real people. Real stories." Fabricated testimonials are a search-trust
              risk of the same kind as the aggregateRating markup we removed. */}
          <HomePage
            onSearch={(loc) => {
              if (loc && loc.name) setInitialLocation(loc);
              handleNavigate('dashboard');
            }}
            onNavigate={handleNavigate}
            /* Place links are real <a href> elements so a crawler can follow
               them; this keeps the click client-side without losing the URL. */
            onPlace={(place) => {
              setInitialLocation({ name: place.name, lat: place.lat, lng: place.lng });
              window.history.pushState({}, '', `/flats-in-${place.slug}`);
              setPathname(`/flats-in-${place.slug}`);
              setCurrentRoute('dashboard');
              window.scrollTo(0, 0);
            }}
          />
          <ContactForm />
        </main>
      )}

      {!isFullscreen && currentRoute !== 'details' && <FooterNav onNavigate={handleNavigate} />}
      <AppDownloadBanner />

      <ChatWidget />
      <PrimeModalHost />
    </>
  );
};

// Injection of Context
export default function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}
